import { useCallback, useEffect, useRef, useState } from "react";
import { BaseControlledModal } from "../../../../../../common/components/base_controlled_modal";
import Webcam from "react-webcam";
import { FaceDetection, Results } from "@mediapipe/face_detection";
import { Camera } from "@mediapipe/camera_utils";
type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  onClose: () => void;
  onError: (message: string) => void;
  onSubmit: (foto: string) => Promise<void>;
};

export const ValidacaoFuncionarioModal = ({
  open,
  saving,
  loading,
  onClose,
  onError,
  onSubmit,
}: Props) => {
  const webcamRef = useRef<Webcam>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [instrucao, setInstrucao] = useState("Posicione seu rosto na câmera");
  const [podeCadastrar, setPodeCadastrar] = useState(false);

  useEffect(() => {
    if (!open) return;

    let isActive = true;
    let camera: Camera | null = null;
    let faceDetection: FaceDetection | null = null;
    let timeoutId: ReturnType<typeof setTimeout>;

    const inicializarIA = async () => {
      if (!isActive) return;

      const videoElement = webcamRef.current?.video;
      if (!videoElement) return;

      faceDetection = new FaceDetection({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${file}`,
      });

      faceDetection.setOptions({
        model: "short",
        minDetectionConfidence: 0.5,
      });

      faceDetection.onResults((results: Results) => {
        if (!isActive) return;

        const canvasElement = canvasRef.current;
        const canvasCtx = canvasElement?.getContext("2d");

        if (!canvasElement || !canvasCtx || !videoElement) return;

        canvasElement.width = videoElement.videoWidth;
        canvasElement.height = videoElement.videoHeight;
        canvasCtx.clearRect(0, 0, canvasElement.width, canvasElement.height);

        if (results.detections.length > 0) {
          const deteccao = results.detections[0];
          const boundingBox = deteccao.boundingBox;
          const landmarks = deteccao.landmarks;

          const proporcaoRosto = boundingBox.width * boundingBox.height;
          let isFaceGood = false;

          // --- LÓGICA DE DISTÂNCIA ---
          if (proporcaoRosto < 0.18) {
            setInstrucao("Aproxime mais o seu rosto");
            setPodeCadastrar(false);
          } else if (proporcaoRosto > 0.6) {
            setInstrucao("Afaste um pouco o rosto");
            setPodeCadastrar(false);
          } else {
            // --- LÓGICA DE ALINHAMENTO FRONTAL ---
            let isLookingStraight = true;

            if (landmarks && landmarks.length >= 4) {
              const rightEye = landmarks[0];
              const leftEye = landmarks[1];
              const nose = landmarks[2];
              const mouth = landmarks[3];

              const eyeDeltaY = Math.abs(rightEye.y - leftEye.y);
              const eyeDeltaX = Math.abs(rightEye.x - leftEye.x) || 0.001;
              const eyeTilt = eyeDeltaY / eyeDeltaX;

              const eyeCenterX = (rightEye.x + leftEye.x) / 2;
              const noseDeviation = Math.abs(nose.x - eyeCenterX) / eyeDeltaX;

              const eyeCenterY = (rightEye.y + leftEye.y) / 2;
              const eyeToNose = nose.y - eyeCenterY;
              const noseToMouth = mouth.y - nose.y || 0.001;
              const verticalRatio = eyeToNose / noseToMouth;

              if (
                eyeTilt > 0.25 ||
                noseDeviation > 0.3 ||
                verticalRatio < 0.6 ||
                verticalRatio > 2.0
              ) {
                isLookingStraight = false;
              }
            }

            if (!isLookingStraight) {
              setInstrucao("Olhe diretamente para a câmera");
              setPodeCadastrar(false);
            } else {
              setInstrucao("A face está com boa qualidade");
              setPodeCadastrar(true);
              isFaceGood = true;
            }
          }
          // -----------------------------------------------------------------

          const largura = boundingBox.width * canvasElement.width;
          const altura = boundingBox.height * canvasElement.height;
          const x = boundingBox.xCenter * canvasElement.width - largura / 2;
          const y = boundingBox.yCenter * canvasElement.height - altura / 2;

          canvasCtx.beginPath();
          canvasCtx.lineWidth = 4;
          canvasCtx.strokeStyle = isFaceGood ? "#84BD42" : "#e50914";
          canvasCtx.setLineDash([10, 10]);
          canvasCtx.rect(x, y, largura, altura);
          canvasCtx.stroke();
          canvasCtx.setLineDash([]);
        } else {
          setInstrucao("Nenhum rosto detectado");
          setPodeCadastrar(false);
        }
      });

      camera = new Camera(videoElement, {
        onFrame: async () => {
          if (!isActive || !faceDetection) return;

          try {
            await faceDetection.send({ image: videoElement });
          } catch (error) {
            console.warn("Frame ignorado na desmontagem do componente.");
          }
        },
        width: 1280,
        height: 720,
      });

      if (isActive) camera.start();
    };

    timeoutId = setTimeout(inicializarIA, 500);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);

      if (camera) camera.stop();
      if (faceDetection) {
        try {
          faceDetection.close();
        } catch (e) {}
      }
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setInstrucao("Posicione seu rosto na câmera");
      setPodeCadastrar(false);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    if (!podeCadastrar) return;

    try {
      const imageSrc = webcamRef.current?.getScreenshot();
      if (!imageSrc) {
        onError("Não foi possível capturar a imagem da câmera.");
        return;
      }

      const base64Payload = imageSrc.split(",")[1];

      await onSubmit(base64Payload);
    } catch (e: any) {
      setPodeCadastrar(false);
      
      const errorMsg = e?.response?.data?.message?.[0] || e?.message || "Falha ao validar a qualidade da face na Gryfo!";
      onError(errorMsg);
    }
  }, [onSubmit, onError, podeCadastrar]);

  return (
    <BaseControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title="Confirmação de recebimento"
      width="650px"
      confirmButtonLabel={
        podeCadastrar ? "Cadastre" : "Aguardando..."
      }
      confirmSavingButtonLabel="Validando..."
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col justify-center items-center py-4 gap-4">
        <div className="w-full flex flex-col items-center gap-4">
          {/* AVISO VISUAL */}
          <div className="bg-amber-100 border border-amber-400 text-amber-800 text-sm px-4 py-3 rounded-md w-full max-w-[600px] text-center shadow-sm">
            <span className="font-bold">⚠️ Atenção:</span> Para validação,
            retire <strong>óculos escuros</strong>, <strong>bonés</strong> ou{" "}
            <strong>máscaras</strong>.
          </div>

          <div className="text-center mb-2">
            <p className="text-sm italic font-semibold text-gray-500">
              instruções:
            </p>
            <h3 className="text-xl font-bold text-blue-800">{instrucao}</h3>
          </div>
        </div>

        {/* VÍDEO SEMPRE ATIVO */}
        <div style={{ position: "relative", width: "100%", maxWidth: 600 }}>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            screenshotQuality={1}
            videoConstraints={{
              facingMode: "user",
              width: 1280,
              height: 720,
            }}
            style={{
              display: "block",
              width: "100%",
              borderRadius: 8,
            }}
          />
          <canvas
            ref={canvasRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: 10,
            }}
          />
        </div>
      </div>
    </BaseControlledModal>
  );
};